import {
  AppBar,
  Box,
  Drawer,
  IconButton,
  Theme,
  Toolbar,
  Typography,
  useMediaQuery,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import { ReactNode, useState } from "react";
import {
  LeftSideNav,
  LoggedOutNav,
} from "#next/components/navigation/LeftSideNav";
import { useUser } from "@auth0/nextjs-auth0/client";
import theme from "#next/theme";
import Image from "next/image";
import Link from "next/link";
import { SxProps } from "@mui/system";

const drawerWidth = 200;

export const DefaultLayout = ({ children }: { children: ReactNode }) => {
  const { user } = useUser();
  const [menuOpen, setMenuOpen] = useState(false);

  function Logo() {
    return (
      <Link href="/home" style={{ textDecoration: "none" }}>
        <Box display="flex" justifyContent="center" alignItems="center">
          <Image
            src="/images/pyrraLogo.jpg"
            width={45}
            height={45}
            alt="Pyrra Technologies logo"
          />
          <Typography
            variant="h1"
            color={theme.palette.secondary.light}
            sx={{ px: 1 }}
          >
            PYRRA
          </Typography>
        </Box>
      </Link>
    );
  }

  const mobileOnly: SxProps<Theme> = { display: { xs: "flex", md: "none" } };
  const desktopOnly: SxProps<Theme> = { display: { xs: "none", md: "flex" } };
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        height: { xs: "100vh", md: "reset" },
        overflow: "scroll",
      }}
    >
      <AppBar
        position={"sticky"}
        sx={{ ...mobileOnly, backgroundImage: "none", width: "100%", left: 0 }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={() => setMenuOpen(!menuOpen)}
            edge="start"
          >
            <MenuIcon />
          </IconButton>
          <Logo />
        </Toolbar>
      </AppBar>
      <Drawer
        variant={isDesktop ? "permanent" : "persistent"}
        anchor={isDesktop ? "left" : "top"}
        sx={{
          width: isDesktop ? drawerWidth : "100vw",
          mr: isDesktop ? 1 : "inherit",
          flexShrink: 0,
          flexDirection: "column",
          zIndex: theme.zIndex.appBar - 1,
          "& .MuiDrawer-paper": {
            width: isDesktop ? drawerWidth : "100vw",
            boxSizing: "border-box",
            borderColor: theme.palette.cyan.main,
            boxShadow: `1px 0px 10px -5px ${theme.palette.cyan.main}`,
            borderRadius: 1,
            borderWidth: 0.1,
          },
        }}
        open={isDesktop ? true : menuOpen}
      >
        <Toolbar>
          <Box sx={desktopOnly}>
            <Logo />
          </Box>
        </Toolbar>
        {user ? (
          <LeftSideNav
            onSelect={() => (isDesktop ? undefined : setMenuOpen(false))}
          />
        ) : (
          <LoggedOutNav
            onSelect={() => (isDesktop ? undefined : setMenuOpen(false))}
          />
        )}
      </Drawer>
      <Box
        sx={{
          paddingLeft: 1,
          paddingRight: 1,
          width: { md: `calc(100% - ${drawerWidth}px)` },
          margin: "0 auto",
          height: "100vh",
        }}
      >
        {children}
      </Box>
    </Box>
  );
};
